import { useState, type ReactNode } from "react";
import { Upload } from "lucide-react";
import { Button, Card, Chip } from "@/components/ds";
import EmployeesTab from "@/components/corporate/organizations/EmployeesTab";
import { DepartmentsTab } from "@/components/corporate/organizations/DepartmentsTab";
import LocationsTab from "@/components/corporate/organizations/LocationsTab";
import { CompaniesTab } from "@/components/corporate/organizations/CompaniesTab";
import { EmployeeImportDialog } from "@/components/corporate/organizations/access/EmployeeImportDialog";

interface OrgPeopleTabProps {
  organizationId: string;
}

export const STRUCTURE_LABEL = "Managed by HR or Microsoft sync";

function StructureList({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card className="min-w-0 px-4 py-3.5">
      <h3 className="mb-2 text-[14px] font-semibold text-ds-ink">{title}</h3>
      {children}
    </Card>
  );
}

/**
 * People: the employees (list + the employee panel, details only), the read-only Removed employees
 * card, "Import employees" (today's import, on the client's behalf) and the company's Structure —
 * departments, locations and companies, read-only here.
 */
export function OrgPeopleTab({ organizationId }: OrgPeopleTabProps) {
  const [importOpen, setImportOpen] = useState(false);

  return (
    <div className="space-y-6" data-testid="org-people">
      <section className="space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-[15px] font-semibold text-ds-ink">Employees</h2>
          <div className="ml-auto">
            <Button icon={<Upload className="h-4 w-4" aria-hidden="true" />} onClick={() => setImportOpen(true)}>
              Import employees
            </Button>
          </div>
        </div>
        <EmployeesTab organizationId={organizationId} />
      </section>

      <section className="space-y-3" data-testid="org-people-structure">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-[15px] font-semibold text-ds-ink">Structure</h2>
          <Chip tone="neutral">{STRUCTURE_LABEL}</Chip>
        </div>
        <div className="grid gap-4 xl:grid-cols-3">
          <StructureList title="Departments">
            <DepartmentsTab organizationId={organizationId} />
          </StructureList>
          <StructureList title="Locations">
            <LocationsTab organizationId={organizationId} />
          </StructureList>
          <StructureList title="Companies">
            <CompaniesTab organizationId={organizationId} />
          </StructureList>
        </div>
      </section>

      <EmployeeImportDialog organizationId={organizationId} open={importOpen} onOpenChange={setImportOpen} />
    </div>
  );
}

export default OrgPeopleTab;
